import type { ReactElement, ReactNode } from "react";
import { useId } from "react";

import { CustomTooltip } from "./CustomTooltip";

interface BlockedTooltipProps {
  isBlocked: boolean;
  reason: ReactNode;
  children: ReactElement<{ "aria-label"?: string }>;
  placement?: "top" | "bottom" | "left" | "right";
  className?: string;
  /** accessible name for the blocked control; needed when the child is not
   * named by visible text or its own aria-label (e.g. a composite child) */
  label?: string;
}

export function BlockedTooltip({
  isBlocked,
  reason,
  children,
  placement = "bottom",
  className,
  label,
}: Readonly<BlockedTooltipProps>) {
  const labelId = useId();
  // an icon-only child is named by aria-label, which name-from-content
  // cannot reach through the hidden subtree: mirror it on the wrapper
  const wrapperLabel = label ?? children.props["aria-label"];

  if (!isBlocked) return children;

  return (
    <CustomTooltip content={reason} placement={placement}>
      {/* the wrapper is the single control exposed to assistive tech: the
          blocked child cannot meaningfully receive focus, so the span carries
          the interactive role react-aria's <Focusable> requires, is named by
          the hidden child (aria-labelledby resolves referenced hidden nodes),
          and reports the disabled state itself */}
      {/* oxlint-disable jsx-a11y/prefer-tag-over-role -- a real button cannot nest the blocked child control the tooltip attaches to */}
      <span
        role="button"
        tabIndex={0}
        aria-disabled="true"
        aria-label={wrapperLabel}
        aria-labelledby={wrapperLabel === undefined ? labelId : undefined}
        // aria-disabled does not suppress browser defaults, so Space would
        // scroll the page while focus sits on the blocked control
        onKeyDown={(event) => {
          if (event.key === " ") event.preventDefault();
        }}
        className={`inline-flex ${className ?? ""}`}
      >
        {/* inert keeps a child that is blocked but not natively disabled
            from taking keyboard focus inside the hidden subtree */}
        <span id={labelId} aria-hidden="true" inert className="contents">
          {children}
        </span>
      </span>
      {/* oxlint-enable jsx-a11y/prefer-tag-over-role */}
    </CustomTooltip>
  );
}
